<template>
  <div class="foodleap-pricing antialiased">
    <!-- Hero Section -->
    <section class="relative overflow-hidden pt-20 pb-32 md:pt-32 md:pb-48 bg-fp-background">
      <div class="max-w-7xl mx-auto px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div class="z-10 animate-on-scroll">
          <div
            class="tw-inline-flex w-fit tw-flex flex-row items-center gap-2 px-3 py-1 rounded-full bg-fp-secondary-container text-fp-on-secondary-fixed-variant text-xs font-semibold mb-6">
            <Icon name="line-graph" size="16"/>
            <p>TRANSITION INTELLIGENCE 2.0</p>
          </div>
          <h1 class="text-5xl md:text-7xl font-bold tracking-tight text-fp-on-surface mb-8 leading-[1.1]">
            Stay ahead in the <br/><span class="text-gradient-primary">food system transition</span>
          </h1>
          <p class="text-lg md:text-xl text-fp-on-surface-variant max-w-xl mb-10 leading-relaxed">
            Access real-time insights on where the market is moving, powered by 450+ organisations and live ecosystem data.
          </p>
          <div class="tw-flex flex-wrap gap-4">
            <button
              @click="$router.push('/deep-dive-report')"
              class="bg-gradient-primary text-white px-8 py-4 rounded-full font-semibold fp-btn-primary active:scale-95">
              Get sample report
            </button>
            <button
              @click="$router.push({ path: '/about', hash: '#contact' })"
              class="bg-fp-surface-container-highest text-fp-on-surface px-8 py-4 rounded-full font-semibold hover:bg-fp-surface-container-high transition-colors active:scale-95">
              Talk to us
            </button>
          </div>
        </div>
        <div class="relative lg:h-[600px] tw-flex items-center justify-center animate-on-scroll delay-2">
          <div class="absolute inset-0 bg-fp-primary-5 rounded-xl blur-3xl transform rotate-6"></div>
          <div class="relative w-full aspect-square md:aspect-video lg:aspect-square rounded-full shadow-ambient overflow-hidden">
            <!-- Innovation Radar: relative+w-full+padding-top:100% = square fill (same pattern as InnovationRadarDetail) -->
            <div
              class="relative w-full rounded-full"
              style="padding-top:100%;background:radial-gradient(circle,rgba(248,250,251,0.95) 0%,rgba(217,226,218,0.2) 60%,#f0f4f0 100%);border:1px solid rgba(217,226,218,0.7);"
            >
              <div class="absolute inset-0">
                <!-- Concentric rings -->
                <div class="absolute inset-0 tw-flex items-center justify-center pointer-events-none">
                  <div class="absolute rounded-full" style="width:80%;height:80%;border:1px solid rgb(213 213 213);"/>
                  <div class="absolute rounded-full" style="width:60%;height:60%;border:1px solid rgb(213 213 213);"/>
                  <div class="absolute rounded-full" style="width:40%;height:40%;border:1px solid rgb(213 213 213);"/>
                  <div class="absolute rounded-full" style="width:20%;height:20%;border:1px solid rgb(213 213 213);"/>
                </div>
                <!-- Axis lines -->
                <div class="absolute" style="top:50%;left:0;right:0;height:1px;background:rgb(213 213 213);"/>
                <div class="absolute" style="left:50%;top:0;bottom:0;width:1px;background:rgb(213 213 213);"/>
                <!-- Axis labels -->
                <span class="absolute pointer-events-none"
                      style="top:50%;left:8px;transform:translateY(-50%);font-size:9px;color:rgba(33,36,74,0.35);font-weight:600;font-family:Inter,sans-serif;">Early</span>
                <span class="absolute pointer-events-none"
                      style="top:50%;right:8px;transform:translateY(-50%);font-size:9px;color:rgba(33,36,74,0.35);font-weight:600;font-family:Inter,sans-serif;">Orchestrating</span>
                <span class="absolute pointer-events-none"
                      style="top:8px;left:50%;transform:translateX(-50%);font-size:9px;color:rgba(33,36,74,0.35);font-weight:600;font-family:Inter,sans-serif;">Critical</span>
                <span class="absolute pointer-events-none"
                      style="bottom:8px;left:50%;transform:translateX(-50%);font-size:9px;color:rgba(33,36,74,0.35);font-weight:600;font-family:Inter,sans-serif;">Niche</span>
                <!-- Dots -->
                <div
                  v-for="dot in pricingDots"
                  :key="dot.actorId + '-' + dot.label"
                  class="absolute rounded-full cursor-pointer"
                  :style="{
                    left: dot.x + '%',
                    top: (100 - dot.y) + '%',
                    width: (dot.size + 2) + 'px',
                    height: (dot.size + 2) + 'px',
                    backgroundColor: pricingVcColor(dot.label),
                    transform: hoveredPricingDot && hoveredPricingDot.actorId === dot.actorId ? 'translate(-50%,-50%) scale(1.8)' : 'translate(-50%,-50%)',
                    zIndex: hoveredPricingDot && hoveredPricingDot.actorId === dot.actorId ? 20 : 1,
                    opacity: 0.85,
                    transition: 'transform 0.15s',
                  }"
                  @mouseenter="hoveredPricingDot = dot"
                  @mouseleave="hoveredPricingDot = null"
                />
                <!-- Tooltip -->
                <div
                  v-if="hoveredPricingDot"
                  class="absolute z-30 pointer-events-none rounded-lg shadow-lg"
                  style="padding:3px 8px;font-size:10px;font-weight:600;background:rgba(33,36,74,0.92);white-space:nowrap;font-family:Inter,sans-serif;color:white;"
                  :style="pricingTooltipStyle(hoveredPricingDot)"
                >
                  {{ hoveredPricingDot.name }}
                </div>
              </div>
            </div>
            <!-- Header overlay -->
            <div class="absolute top-4 left-4 right-4 tw-flex justify-between items-center z-20 pointer-events-none">
              <!--              <div>
                              <p class="text-xs font-bold tracking-widest uppercase m-0" style="color:rgba(33,36,74,0.5);font-family:Inter,sans-serif;">Innovation Radar</p>
                              <p class="text-sm font-semibold m-0" style="color:#21244A;font-family:Inter,sans-serif;">{{ pricingDots.length }} organisations</p>
                            </div>
                            <div class="tw-flex items-center gap-1" style="color:#5CCC61;">
                              <span class="material-symbols-outlined" style="font-size:12px;line-height:1;">fiber_manual_record</span>
                              <span class="text-xs font-semibold" style="font-family:Inter,sans-serif;">Live</span>
                            </div>-->
            </div>
            <!-- Legend overlay -->
            <!--            <div class="absolute bottom-3 left-4 right-4 z-20">
                          <div class="tw-flex flex-wrap gap-x-3 gap-y-0.5">
                            <div v-for="cat in pricingCategories" :key="cat.label" class="tw-flex items-center gap-1">
                              <div class="rounded-full" style="width:6px;height:6px;flex-shrink:0;" :style="{ backgroundColor: cat.color }"></div>
                              <span style="font-size:8.5px;color:rgba(33,36,74,0.6);font-weight:500;white-space:nowrap;font-family:Inter,sans-serif;">{{ cat.label }}</span>
                            </div>
                          </div>
                        </div>-->
          </div>
        </div>
      </div>
    </section>

    <!-- Innovation Challenges Block -->
    <section class="py-24 bg-fp-background border-b border-fp-outline-variant-10">
      <div class="max-w-7xl mx-auto px-8">
        <div class="grid lg:grid-cols-2 gap-16 items-center">
          <div class="animate-on-scroll">
            <div class="tw-inline-flex w-fit items-center gap-2 px-3 py-1 rounded-full bg-fp-secondary-container text-fp-on-secondary-fixed-variant text-xs font-semibold mb-6">
              <Icon name="light-bulb" size="14"/>
              Open Innovation
            </div>
            <h2 class="text-3xl md:text-4xl font-bold tracking-tight text-fp-on-surface mb-4 leading-tight">
              Innovation Challenges
            </h2>
            <p class="text-fp-on-surface-variant text-lg leading-relaxed mb-8">
              Co-design open innovation challenges with startups across the food transition ecosystem. We handle the full process, from brief design to partner selection and pilot
              coordination.
            </p>
            <ul class="space-y-4 mb-10">
              <li class="tw-flex items-start gap-3">
                <Icon name="check-circle" size="18" class="text-fp-secondary mt-0.5"/>
                <div>
                  <span class="font-semibold text-fp-on-surface">Ecosystem search</span>
                  <span class="text-fp-on-surface-variant"> — 450+ food innovation orgs screened to your brief</span>
                </div>
              </li>
              <li class="tw-flex items-start gap-3">
                <Icon name="check-circle" size="18" class="text-fp-secondary mt-0.5"/>
                <div>
                  <span class="font-semibold text-fp-on-surface">Partner matchmaking</span>
                  <span class="text-fp-on-surface-variant"> — curated shortlist of aligned innovators</span>
                </div>
              </li>
              <li class="tw-flex items-start gap-3">
                <Icon name="check-circle" size="18" class="text-fp-secondary mt-0.5"/>
                <div>
                  <span class="font-semibold text-fp-on-surface">Managed pilot design</span>
                  <span class="text-fp-on-surface-variant"> — structured 6–8 week pilot framework</span>
                </div>
              </li>
              <li class="tw-flex items-start gap-3">
                <Icon name="check-circle" size="18" class="text-fp-secondary mt-0.5"/>
                <div>
                  <span class="font-semibold text-fp-on-surface">Results reporting</span>
                  <span class="text-fp-on-surface-variant"> — clear outcomes and next-step recommendations</span>
                </div>
              </li>
            </ul>
            <div class="tw-flex items-center gap-6">
              <!--              <div>
                              <div class="text-3xl font-black text-fp-on-surface">€7,500</div>
                              <div class="text-fp-on-surface-variant text-sm">per challenge</div>
                            </div>-->
              <button @click="$router.push({ path: '/about', hash: '#contact' })"
                      class="bg-gradient-primary text-white px-8 py-3.5 rounded-full font-semibold fp-btn-primary active:scale-95">
                Talk to us
              </button>
            </div>
          </div>
          <div class="animate-on-scroll delay-2">
            <div class="bg-fp-surface-container-low rounded-xl p-8 border border-fp-outline-variant-10">
              <div class="text-xs font-bold tracking-widest uppercase text-fp-on-surface-variant mb-6">How a challenge works</div>
              <div class="space-y-4">
                <div v-for="(step, i) in challengeSteps" :key="i" class="tw-flex items-start gap-4">
                  <div class="w-8 h-8 rounded-full bg-fp-secondary-container-30 tw-flex items-center justify-center shrink-0 text-xs font-black text-fp-secondary">{{ i + 1 }}</div>
                  <div>
                    <div class="font-semibold text-fp-on-surface text-sm">{{ step.title }}</div>
                    <div class="text-fp-on-surface-variant text-xs mt-0.5">{{ step.desc }}</div>
                  </div>
                </div>
              </div>
              <div class="mt-8 pt-6 border-t border-fp-outline-variant-10 tw-flex items-center gap-6 text-center">
                <div class="flex-1">
                  <div class="text-2xl font-black text-fp-on-surface">450+</div>
                  <div class="text-fp-on-surface-variant text-xs">innovators screened</div>
                </div>
                <div class="w-px h-8 bg-fp-outline-variant-10"></div>
                <div class="flex-1">
                  <div class="text-2xl font-black text-fp-on-surface">6–8</div>
                  <div class="text-fp-on-surface-variant text-xs">weeks to pilot</div>
                </div>
                <div class="w-px h-8 bg-fp-outline-variant-10"></div>
                <div class="flex-1">
                  <div class="text-2xl font-black text-fp-on-surface">14</div>
                  <div class="text-fp-on-surface-variant text-xs">countries covered</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Product Breakdown -->
    <section class="py-24 bg-fp-surface-container-low">
      <div class="max-w-7xl mx-auto px-8">
        <div class="tw-flex flex-col md:flex-row justify-between items-end mb-16 gap-6 animate-on-scroll">
          <div>
            <h2 class="text-3xl md:text-4xl font-bold tracking-tight mb-4">Transition Reports</h2>
            <p class="text-fp-on-surface-variant max-w-md">Our three-tier approach ensures you never miss a tactical shift or a strategic opportunity.</p>
          </div>
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          <div
            @click="onReportClick('Transition Pulse', '/transition-pulse-report')"
            class="bg-fp-surface-container-lowest p-8 rounded-lg shadow-ambient border border-fp-outline-variant-10 group hover:-translate-y-2 hover:shadow-2xl hover:border-fp-yellow transition-all animate-on-scroll delay-1 cursor-pointer"
            data-content-name="Foodleap Report Card" data-content-piece="Transition Pulse">
            <div class="w-14 h-14 rounded-md bg-fp-yellow-10 tw-flex items-center justify-center mb-8">
              <Icon name="wave-square" size="28" class="text-fp-yellow"/>
            </div>
            <h3 class="text-xl font-bold mb-2">Transition Pulse</h3>
            <div class="text-fp-yellow font-semibold text-sm mb-4 uppercase tracking-wider">Monthly</div>
            <p class="text-fp-on-surface-variant mb-6">What is moving right now. A high-frequency analysis of market shifts, regulatory updates, and emerging startups.</p>
            <span class="tw-inline-flex items-center gap-1 text-fp-primary font-semibold tw-flex flex-row">
              View Sample <Icon name="chevron-right" class="self-center group-hover:translate-x-1 transition-transform"/>
            </span>
          </div>
          <div
            @click="onReportClick('Innovation Radar', '/innovation-radar-report')"
            class="bg-fp-surface-container-lowest p-8 rounded-lg shadow-ambient border border-fp-outline-variant-10 group hover:-translate-y-2 hover:shadow-2xl hover:border-fp-green transition-all animate-on-scroll delay-2 cursor-pointer"
            data-content-name="Foodleap Report Card" data-content-piece="Innovation Radar">
            <div class="w-14 h-14 rounded-md bg-fp-green-10 tw-flex items-center justify-center mb-8">
              <Icon name="bullseye" size="28" class="text-fp-secondary"/>
            </div>
            <h3 class="text-xl font-bold mb-2">Innovation Radar</h3>
            <div class="text-fp-secondary font-semibold text-sm mb-4 uppercase tracking-wider">Quarterly</div>
            <p class="text-fp-on-surface-variant mb-6">Where to act next. Mapping white spaces and competitive movements to guide your next investment or R&D cycle.</p>
            <span class="tw-inline-flex items-center gap-1 text-fp-primary font-semibold tw-flex flex-row">
              View Sample <Icon name="chevron-right" class="self-center group-hover:translate-x-1 transition-transform"/>
            </span>
          </div>
          <div
            @click="onReportClick('Deep Dive', '/deep-dive-report')"
            class="bg-fp-surface-container-lowest p-8 rounded-lg shadow-ambient border border-fp-outline-variant-10 group hover:-translate-y-2 hover:shadow-2xl hover:border-fp-blue transition-all animate-on-scroll delay-3 cursor-pointer"
            data-content-name="Foodleap Report Card" data-content-piece="Deep Dive">
            <div class="w-14 h-14 rounded-md bg-fp-blue-10 tw-flex items-center justify-center mb-8">
              <Icon name="file-text" size="28" class="text-fp-blue"/>
            </div>
            <h3 class="text-xl font-bold mb-2">Deep Dive Reports</h3>
            <div class="text-fp-blue font-semibold text-sm mb-4 uppercase tracking-wider">Annual</div>
            <p class="text-fp-on-surface-variant mb-6">Comprehensive vertical studies. From Alternative Proteins to Circular Packaging, we go deep into the mechanics.</p>
            <span class="tw-inline-flex items-center gap-1 text-fp-primary font-semibold tw-flex flex-row">
              Get first one free <Icon name="chevron-right" class="self-center group-hover:translate-x-1 transition-transform"/>
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- Deep Dive Promo -->
    <section class="py-24 overflow-hidden bg-fp-background">
      <div class="max-w-7xl mx-auto px-8">
        <div class="bg-fp-deep-dark rounded-xl overflow-hidden tw-flex flex-col md:flex-row shadow-2xl animate-on-scroll">
          <!-- Left: Report info -->
          <div class="flex-1 p-10 md:p-16 tw-flex flex-col justify-center">
            <div class="tw-inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fp-white-10 text-fp-white-90 text-xs font-bold tracking-widest uppercase mb-8 w-fit">
              <Icon name="file-text" size="12" fill="#B5F167"/>
              Free Deep Dive Report
            </div>
            <h3 class="text-white text-3xl font-bold mb-4 leading-tight">Balanced Proteins: The Next Phase of the Protein Transition</h3>
            <!--            <p class="text-fp-white-90 text-sm leading-relaxed mb-8 opacity-70">A comprehensive analysis of the alternative protein landscape — from precision fermentation
                          breakthroughs to plant-based market dynamics and investment flows across 14 European markets.</p>-->
            <div class="space-y-3">
              <div class="bg-fp-white-10 backdrop-blur-md p-4 rounded-md border border-fp-white-5 tw-flex items-center gap-4">
                <Icon name="arrow-graph" fill="#B5F167"/>
                <span class="text-fp-white-90 text-sm">Market growth, investment & M&A trends in balanced proteins</span>
              </div>
              <div class="bg-fp-white-10 backdrop-blur-md p-4 rounded-md border border-fp-white-5 tw-flex items-center gap-4">
                <Icon name="star" fill="#B5F167"/>
                <span class="text-fp-white-90 text-sm">Key players, innovations & product launches shaping the landscape</span>
              </div>
              <div class="bg-fp-white-10 backdrop-blur-md p-4 rounded-md border border-fp-white-5 tw-flex items-center gap-4">
                <Icon name="shield" fill="#B5F167"/>
                <span class="text-fp-white-90 text-sm">Consumer Insights & adoption across regions</span>
              </div>
            </div>
          </div>
          <!-- Right: Locked Preview -->
          <div class="flex-1 relative min-h-[400px] tw-flex items-center justify-center">
            <div
              class="absolute inset-0 bg-cover bg-center blur-md opacity-40 scale-110 grayscale"
            ></div>
            <div class="relative glass-panel p-8 rounded-lg border border-fp-white-20 text-center max-w-xs z-10 shadow-2xl">
              <div class="w-16 h-16 bg-fp-primary-container rounded-full mx-auto tw-flex items-center justify-center mb-6">
                <Icon name="lock" size="28"/>
              </div>
              <h4 class="text-fp-on-surface font-bold text-xl mb-4">Balanced Proteins <br> Deep Dive Report</h4>
              <p>A comprehensive analysis of the market, players, innovations and future outlook.</p>
              <div class="tw-flex flex-col gap-3">
                <button disabled class="py-3 px-6 rounded-full font-bold cursor-not-allowed opacity-50 border border-fp-outline-variant-10 text-fp-on-surface-variant">Coming soon
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Advisory Block -->
    <section class="py-24 bg-fp-surface-container-low border-t border-fp-outline-variant-10">
      <div class="max-w-7xl mx-auto px-8">
        <div class="grid lg:grid-cols-2 gap-16 items-center">
          <div class="animate-on-scroll order-2 lg:order-1">
            <div class="bg-fp-deep-dark rounded-xl p-8 space-y-4">
              <div class="text-xs font-bold tracking-widest uppercase text-white/40 mb-6">What advisory includes</div>
              <div v-for="(item, i) in advisoryItems" :key="i" class="tw-flex items-start gap-4 p-4 rounded-lg" style="background-color: rgba(255,255,255,0.05);">
                <div class="w-9 h-9 rounded-lg tw-flex items-center justify-center shrink-0" style="background-color: rgba(255,255,255,0.08);">
                  <Icon :name="item.icon" size="16" class="text-fp-secondary"/>
                </div>
                <div>
                  <div class="font-semibold text-white text-sm">{{ item.title }}</div>
                  <div class="text-white/50 text-xs mt-0.5">{{ item.desc }}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="animate-on-scroll delay-1 order-1 lg:order-2">
            <div class="tw-inline-flex w-fit items-center gap-2 px-3 py-1 rounded-full bg-fp-secondary-container text-fp-on-secondary-fixed-variant text-xs font-semibold mb-6">
              <Icon name="hand-shake" size="14"/>
              Advisory
            </div>
            <h2 class="text-3xl md:text-4xl font-bold tracking-tight text-fp-on-surface mb-4 leading-tight">
              Strategic guidance for the food transition
            </h2>
            <p class="text-fp-on-surface-variant text-lg leading-relaxed mb-8">
              Bespoke strategy work built on top of our ecosystem intelligence. From transition roadmaps to executive coaching and custom deep dives, tailored to where you are in
              your journey.
            </p>
            <ul class="space-y-4 mb-10">
              <li class="tw-flex items-start gap-3">
                <Icon name="check-circle" size="18" class="text-fp-secondary mt-0.5"/>
                <div>
                  <span class="font-semibold text-fp-on-surface">Transition strategy workshops</span>
                  <span class="text-fp-on-surface-variant"> — facilitated sessions with your leadership team</span>
                </div>
              </li>
              <li class="tw-flex items-start gap-3">
                <Icon name="check-circle" size="18" class="text-fp-secondary mt-0.5"/>
                <div>
                  <span class="font-semibold text-fp-on-surface">Bespoke deep dives</span>
                  <span class="text-fp-on-surface-variant"> — custom research reports on your focus verticals</span>
                </div>
              </li>
              <li class="tw-flex items-start gap-3">
                <Icon name="check-circle" size="18" class="text-fp-secondary mt-0.5"/>
                <div>
                  <span class="font-semibold text-fp-on-surface">Executive coaching</span>
                  <span class="text-fp-on-surface-variant"> — 1:1 and team sessions on food system positioning</span>
                </div>
              </li>
            </ul>
            <div class="tw-flex items-center gap-6">
              <!--              <div>
                              <div class="text-3xl font-black text-fp-on-surface">Project</div>
                              <div class="text-fp-on-surface-variant text-sm">based pricing</div>
                            </div>-->
              <button @click="$router.push({ path: '/about', hash: '#contact' })"
                      class="bg-gradient-primary text-white px-8 py-3.5 rounded-full font-semibold fp-btn-primary active:scale-95">
                Talk to us
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Use Cases Section -->
    <section class="py-24 bg-fp-background">
      <div class="max-w-7xl mx-auto px-8">
        <div class="text-center mb-20 animate-on-scroll">
          <h2 class="text-3xl md:text-5xl font-bold tracking-tight mb-4">From insight to action</h2>
          <p class="text-fp-on-surface-variant max-w-2xl mx-auto">Connecting our intelligence with the 450+ organization ecosystem map to drive real progress.</p>
        </div>
        <div class="grid md:grid-cols-3 gap-12">
          <div class="text-center animate-on-scroll delay-1">
            <div class="w-16 h-16 bg-fp-surface-container tw-flex items-center justify-center rounded-full mx-auto mb-6 fp-use-case-icon-wrap">
              <Icon name="bullseye" size="28"/>
            </div>
            <h4 class="text-xl font-bold mb-4">Strategy Teams</h4>
            <p class="text-fp-on-surface-variant">Benchmark your portfolio against transition leaders and de-risk your long-term roadmap.</p>
          </div>
          <div class="text-center animate-on-scroll delay-2">
            <div class="w-16 h-16 bg-fp-surface-container tw-flex items-center justify-center rounded-full mx-auto mb-6 fp-use-case-icon-wrap">
              <Icon name="light-bulb" size="28"/>
            </div>
            <h4 class="text-xl font-bold mb-4">Innovation Teams</h4>
            <p class="text-fp-on-surface-variant">Identify potential R&D partners and discover early-stage technologies before they go mainstream.</p>
          </div>
          <div class="text-center animate-on-scroll delay-3">
            <div class="w-16 h-16 bg-fp-surface-container tw-flex items-center justify-center rounded-full mx-auto mb-6 fp-use-case-icon-wrap">
              <Icon name="credit-card" size="28"/>
            </div>
            <h4 class="text-xl font-bold mb-4">Investment Teams</h4>
            <p class="text-fp-on-surface-variant">Monitor deal flow clusters and track the maturity levels of different technology sub-sectors.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section class="py-24 bg-fp-surface-container-low">
      <div id="#pricing" class="max-w-7xl mx-auto px-8">
        <div class="text-center mb-20 animate-on-scroll">
          <h2 class="text-3xl md:text-5xl font-bold tracking-tight mb-4">Select your level of intelligence</h2>
          <p class="text-fp-on-surface-variant">Scale your access as your transition strategy matures.</p>
        </div>
        <div class="grid md:grid-cols-3 gap-8 items-stretch">
          <!-- Innovation Challenges -->
          <div class="bg-fp-surface-container-lowest p-10 rounded-lg shadow-ambient border border-fp-outline-variant-10 tw-flex flex-col animate-on-scroll delay-1"
               data-content-name="Foodleap Pricing Tier" data-content-piece="Innovation Challenges">
            <h4 class="text-xl font-bold mb-2">Innovation Challenges</h4>
            <div class="text-3xl font-black mb-6">€7,500 <span class="text-base font-medium text-fp-on-surface-variant">/ challenge</span></div>
            <ul class="space-y-4 mb-10 flex-1">
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle"/>
                Ecosystem search
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle"/>
                Partner matchmaking
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle"/>
                Managed pilot design
              </li>
            </ul>
            <button @click="$router.push('/about#contact')"
                    class="w-full py-4 rounded-md border border-fp-outline-variant-10 text-fp-on-surface-variant font-bold cursor-pointer hover:bg-gray-200">Contact Us
            </button>
          </div>
          <!-- Ecosystem Insights (Highlighted) -->
          <div
            class="bg-fp-surface-container-lowest p-10 rounded-lg shadow-2xl border-2 border-fp-primary relative tw-flex flex-col transform scale-105 z-10 animate-on-scroll delay-2"
            data-content-name="Foodleap Pricing Tier" data-content-piece="Ecosystem Insights">
            <div class="absolute -top-4 left-1/2 -translate-x-1/2 bg-fp-primary text-white px-4 py-1 rounded-full text-xs font-black tracking-widest uppercase">Most Popular</div>
            <h4 class="text-xl font-bold mb-2">Ecosystem Insights</h4>
            <div class="text-3xl font-black mb-6">€9,000 <span class="text-base font-medium text-fp-on-surface-variant">/ year</span></div>
            <ul class="space-y-4 mb-10 flex-1">
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle"/>
                Transition Pulse (Monthly)
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle"/>
                Innovation Radar (Quarterly)
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle"/>
                Full Ecosystem Data Access
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle"/>
                Stakeholder directory
              </li>
            </ul>
            <button disabled class="w-full py-4 rounded-md bg-fp-surface-container-high text-fp-on-surface-variant font-bold cursor-not-allowed opacity-50">Coming soon</button>
          </div>
          <!-- Transition Advisory -->
          <div class="bg-fp-surface-container-lowest p-10 rounded-lg shadow-ambient border border-fp-outline-variant-10 tw-flex flex-col animate-on-scroll delay-3"
               data-content-name="Foodleap Pricing Tier" data-content-piece="Transition Advisory">
            <h4 class="text-xl font-bold mb-2">Transition Advisory</h4>
            <div class="text-3xl font-black mb-6">Project <span class="text-base font-medium text-fp-on-surface-variant">based</span></div>
            <ul class="space-y-4 mb-10 flex-1">
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle"/>
                Bespoke deep dives
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle"/>
                Strategy workshop
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle"/>
                Executive coaching
              </li>
            </ul>
            <button @click="$router.push('/about#contact')"
                    class="w-full py-4 rounded-md border border-fp-outline-variant-10 text-fp-on-surface-variant font-bold cursor-pointer hover:bg-gray-200">Talk to us
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <!--    <section class="py-24 relative overflow-hidden bg-fp-background">
          <div class="max-w-4xl mx-auto px-8 text-center relative z-10 animate-on-scroll">
            <h2 class="text-4xl md:text-5xl font-bold mb-8">Start with a sample report</h2>
            <p class="text-lg text-fp-on-surface-variant mb-12">Experience the depth of Foodleap intelligence before committing to a full subscription.</p>
            <div class="tw-flex flex-wrap justify-center gap-4">
              <button @click="$router.push('/deep-dive-report')"
                      class="bg-gradient-primary text-white px-10 py-5 rounded-full font-bold shadow-2xl fp-btn-primary hover:scale-105 transition-transform">Get sample report
              </button>
              <button class="bg-white border border-fp-outline text-fp-on-surface px-10 py-5 rounded-full font-bold hover:bg-fp-surface-container-low transition-colors">Book a demo
              </button>
            </div>
          </div>
          <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-fp-primary-5 rounded-[100%] blur-3xl pointer-events-none"
               style="z-index: -1;"></div>
        </section>-->

    <!-- Footer -->
    <SimplifiedFooterFoodleapNew/>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import SimplifiedFooterFoodleapNew from '@/components/SimplifiedFooter/SimplifiedFooterFoodleapNew.vue'
  import Icon from '../Icon/Icon.vue'
  import { createImpressionObserver, createScrollDepthTracker, trackFoodleapCta, trackFoodleapInteraction, trackFoodleapPageView, } from '@/util/useFoodleapTracking.js'

  export default defineComponent({
    name: 'StaticHomeFoodleapPricing',
    components: { SimplifiedFooterFoodleapNew, Icon },
    data () {
      return {
        challengeSteps: [
          { title: 'Define the brief', desc: 'We work with your team to scope the challenge and criteria.' },
          { title: 'Search the ecosystem', desc: '450+ organisations screened against your requirements.' },
          { title: 'Match & shortlist', desc: 'Curated list of aligned partners presented for review.' },
          { title: 'Run the pilot', desc: 'Structured 6–8 week pilot with full coordination support.' },
        ],
        advisoryItems: [
          { icon: 'light-bulb', title: 'Transition Roadmaps', desc: 'Strategic planning aligned to your sector and ambition level.' },
          { icon: 'file-text', title: 'Bespoke Deep Dives', desc: 'Custom research on the verticals that matter most to your business.' },
          { icon: 'user', title: 'Executive Coaching', desc: 'Structured sessions to build internal food transition capability.' },
        ],
        hoveredPricingDot: null,
        pricingActors: [
          { actorId: 1256725, name: 'PPM Oost NV', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 69, y: 46.8, size: 8 }] },
          { actorId: 1650662, name: 'Alvinesa', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 72, y: 70.6, size: 6 }] },
          { actorId: 1518971, name: 'Royal Smilde', valueChainPositions: [{ label: 'End Products & Brands', x: 68.6, y: 41, size: 12 }] },
          { actorId: 1520366, name: 'MOA', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 59.4, y: 65.6, size: 4 }] },
          { actorId: 1347894, name: 'Bodec', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 67.4, y: 54.4, size: 6 }] },
          { actorId: 1250519, name: 'Biorefinery Solutions', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 54, y: 60, size: 4 }] },
          { actorId: 1643161, name: 'Koa', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 71.4, y: 65.6, size: 10 }] },
          { actorId: 1643159, name: 'Instock Products B.V.', valueChainPositions: [{ label: 'Market Access & Distribution', x: 80.6, y: 76.6, size: 6 }] },
          { actorId: 1650677, name: 'FLUID QUIP TECHNOLOGIES', valueChainPositions: [{ label: 'Processing & Technology', x: 78, y: 57.8, size: 6 }] },
          { actorId: 1256629, name: 'Agrifirm NWE B.V.', valueChainPositions: [{ label: 'Processing & Technology', x: 74.6, y: 54.4, size: 14 }] },
          { actorId: 1256747, name: 'Greencovery®', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 61.2, y: 65.6, size: 4 }] },
          { actorId: 1442798, name: 'Grassa', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 69.4, y: 64.4, size: 6 }] },
          { actorId: 1256729, name: 'Provincie Gelderland', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 70.6, y: 71.2, size: 14 }] },
          { actorId: 1639571, name: 'B2BE Facilitator', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 50.6, y: 51.2, size: 4 }] },
          { actorId: 1652736, name: 'Nature Preserve', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 68.6, y: 53.4, size: 4 }] },
          { actorId: 1256746, name: 'EverGrain Ingredients', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 74.6, y: 65.6, size: 6 }] },
          { actorId: 1442803, name: 'PeelPioneers', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 80.6, y: 81.6, size: 6 }] },
          { actorId: 1334892, name: 'Legria', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 65.4, y: 70, size: 4 }] },
          { actorId: 1652746, name: 'Beyond Leather Materials', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 71.4, y: 60, size: 4 }] },
          { actorId: 1381215, name: 'Danish Technological Institute', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 71.4, y: 67.8, size: 14 }] },
          { actorId: 1272305, name: 'Carbiotix', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 63.4, y: 62.2, size: 4 }] },
          { actorId: 1367904, name: 'Green Spot Technologies', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 66.6, y: 61.6, size: 6 }] },
          { actorId: 1256641, name: 'Herbafood Ingredients GmbH', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 72.6, y: 56.6, size: 8 }] },
          { actorId: 1643036, name: 'Kaffe Bueno', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 78, y: 73.4, size: 6 }] },
          { actorId: 1304668, name: 'Feedvalid', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 77, y: 62.2, size: 8 }] },
          { actorId: 1304672, name: 'NoPalm Ingredients', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 73.4, y: 61.2, size: 6 }] },
          { actorId: 1643162, name: 'Kumasi', valueChainPositions: [{ label: 'End Products & Brands', x: 76, y: 64.4, size: 6 }] },
          { actorId: 1382332, name: 'Re:Start Upcycling', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 50.6, y: 53.4, size: 4 }] },
          { actorId: 1425929, name: 'InsectSense', valueChainPositions: [{ label: 'Processing & Technology', x: 55, y: 42.8, size: 4 }] },
          { actorId: 1652800, name: 'Hub cycle', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 67.6, y: 65.6, size: 6 }] },
          { actorId: 1652803, name: 'Prosur', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 70.6, y: 53.4, size: 6 }] },
          { actorId: 1518809, name: 'FrieslandCampina', valueChainPositions: [{ label: 'End Products & Brands', x: 82.6, y: 58.4, size: 14 }] },
          { actorId: 1256642, name: 'International Flavors & Fragrances', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 86, y: 62.2, size: 14 }] },
          { actorId: 1490324, name: 'KiCo', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 76, y: 51.6, size: 4 }] },
          { actorId: 1352994, name: 'NOI Techpark', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 86, y: 63.4, size: 10 }] },
          { actorId: 1334890, name: 'Meatco', valueChainPositions: [{ label: 'Processing & Technology', x: 62, y: 60, size: 6 }] },
          { actorId: 1342731, name: 'Bakkersgrondstof', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 56.6, y: 63.4, size: 4 }] },
          { actorId: 1503209, name: 'Van Kneus', valueChainPositions: [{ label: 'End Products & Brands', x: 47, y: 43.8, size: 4 }] },
          { actorId: 1604510, name: 'Maltivor', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 80.4, y: 62.2, size: 6 }] },
          { actorId: 1616400, name: 'Upgrain', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 78, y: 72.8, size: 6 }] },
          { actorId: 1643039, name: 'Kost Capital', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 53.4, y: 53.8, size: 4 }] },
          { actorId: 1639730, name: 'SomaTech', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 60.4, y: 57.8, size: 4 }] },
          { actorId: 1639567, name: 'RE:harvest', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 72.6, y: 65.6, size: 6 }] },
          { actorId: 1618331, name: 'Pacha de Cacao', valueChainPositions: [{ label: 'End Products & Brands', x: 65, y: 60, size: 4 }] },
          { actorId: 1643038, name: 'endless food', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 66.6, y: 63.4, size: 4 }] },
          { actorId: 1378500, name: 'Caffe Inc', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 77, y: 67.8, size: 4 }] },
          { actorId: 1386434, name: 'Samen Tegen Voedselverspilling', valueChainPositions: [{ label: 'Market Access & Distribution', x: 68.6, y: 53.8, size: 4 }] },
          { actorId: 1252694, name: 'Uticon (Netherlands)', valueChainPositions: [{ label: 'Processing & Technology', x: 78, y: 56.6, size: 8 }] },
          { actorId: 1553372, name: 'Team Tastemakers', valueChainPositions: [{ label: 'End Products & Brands', x: 54.6, y: 46.6, size: 4 }] },
          { actorId: 1252573, name: 'PRIMEPACT', valueChainPositions: [{ label: 'Market Access & Distribution', x: 69.4, y: 36.6, size: 6 }] },
          { actorId: 1256711, name: 'Symrise', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 84.6, y: 66.6, size: 14 }] },
          { actorId: 1643122, name: 'ChainCraft', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 78, y: 70, size: 6 }] },
          { actorId: 1304674, name: 'Milgro', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 82, y: 58.8, size: 8 }] },
          { actorId: 1581013, name: 'Ingredalia', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 59, y: 62.2, size: 4 }] },
          { actorId: 1613866, name: 'Saledo', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 73.4, y: 72, size: 6 }] },
          { actorId: 1643042, name: 'Eat Wasted', valueChainPositions: [{ label: 'End Products & Brands', x: 67.6, y: 60, size: 6 }] },
          { actorId: 1634231, name: 'PXL8 Powders', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 46.6, y: 56.6, size: 6 }] },
          { actorId: 1634233, name: 'Revive Ingredients', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 70, y: 58.8, size: 6 }] },
          { actorId: 1639562, name: 'Sidestream', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 55.4, y: 53.4, size: 6 }] },
          { actorId: 1639563, name: 'Axians', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 84.6, y: 14.4, size: 8 }] },
          { actorId: 1639569, name: 'Trobas Gelatine BV', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 69, y: 53.8, size: 8 }] },
          { actorId: 1639572, name: 'ONE\\THIRD', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 79.4, y: 60, size: 4 }] },
          { actorId: 1639788, name: 'Upcycling Solutions', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 58.4, y: 65.6, size: 4 }] },
          { actorId: 1652802, name: 'Motai Group', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 40, y: 56.6, size: 4 }] },
          { actorId: 1640519, name: 'Packtin', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 58.6, y: 61.2, size: 4 }] },
          { actorId: 1641266, name: 'GroundUp eco-ventures', valueChainPositions: [{ label: 'End Products & Brands', x: 60.4, y: 66.2, size: 4 }] },
          { actorId: 1644260, name: 'Grounds For Good', valueChainPositions: [{ label: 'End Products & Brands', x: 67.6, y: 61.2, size: 4 }] },
          { actorId: 1643187, name: 'Bakers & Bananas', valueChainPositions: [{ label: 'End Products & Brands', x: 69.4, y: 58.8, size: 6 }] },
          { actorId: 1643046, name: 'No Waste Army', valueChainPositions: [{ label: 'End Products & Brands', x: 75, y: 62.2, size: 4 }] },
          { actorId: 1643852, name: 'Solid Fermentation Innovation', valueChainPositions: [{ label: 'Processing & Technology', x: 60, y: 55.6, size: 6 }] },
          { actorId: 1643192, name: 'Van Eigen Deeg', valueChainPositions: [{ label: 'End Products & Brands', x: 74.6, y: 50, size: 4 }] },
          { actorId: 1652804, name: 'Ex-Seed', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 58, y: 68.8, size: 4 }] },
          { actorId: 1645937, name: 'Spora', valueChainPositions: [{ label: 'Processing & Technology', x: 66, y: 63.8, size: 6 }] },
          { actorId: 1646103, name: 'Fed Up Caribbean Ltd.', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 42.6, y: 54.4, size: 6 }] },
          { actorId: 1647156, name: 'Food Hub SRL SB', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 62.6, y: 46.6, size: 4 }] },
          { actorId: 1650079, name: 'NICE BIOTECH CO. LTD.', valueChainPositions: [{ label: 'End Products & Brands', x: 35.4, y: 17.2, size: 6 }] },
          { actorId: 1650760, name: 'Biasol', valueChainPositions: [{ label: 'End Products & Brands', x: 66, y: 64.4, size: 4 }] },
          { actorId: 1651139, name: 'KTC Ingredients', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 62, y: 42.2, size: 6 }] },
          { actorId: 1652734, name: 'Mycotech', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 61.4, y: 60, size: 4 }] },
          { actorId: 1652735, name: 'Food Experts', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 58.6, y: 49.4, size: 8 }] },
          { actorId: 1652737, name: 'Nordinvent', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 67.4, y: 58.8, size: 4 }] },
          { actorId: 1652738, name: 'Kødbyens is', valueChainPositions: [{ label: 'End Products & Brands', x: 76.6, y: 31.2, size: 6 }] },
          { actorId: 1652742, name: 'Grounded', valueChainPositions: [{ label: 'End Products & Brands', x: 47.6, y: 53.4, size: 6 }] },
          { actorId: 1652741, name: 'Dana Technology', valueChainPositions: [{ label: 'Processing & Technology', x: 64, y: 40.2, size: 6 }] },
          { actorId: 1652743, name: 'Food Organisation of Denmark', valueChainPositions: [{ label: 'Market Access & Distribution', x: 66.6, y: 39.4, size: 6 }] },
          { actorId: 1652801, name: 'Ferm Labs', valueChainPositions: [{ label: 'Ingredients & Intermediates', x: 44.6, y: 44.4, size: 6 }] },
          { actorId: 1652745, name: 'Copenhagen Hospitality College', valueChainPositions: [{ label: 'Enabling Infrastructure', x: 76.6, y: 51.6, size: 10 }] },
          { actorId: 1690963, name: 'Stockable BV', valueChainPositions: [{ label: 'Market Access & Distribution', x: 53.4, y: 53.4, size: 6 }] },
        ],
      }
    },
    computed: {
      pricingDots () {
        return this.pricingActors.flatMap(actor =>
          actor.valueChainPositions.map(pos => ({
            actorId: actor.actorId,
            name: actor.name,
            label: pos.label,
            x: pos.x,
            y: pos.y,
            size: Math.max(5, Math.min(14, pos.size)),
          }))
        )
      },
      pricingCategories () {
        const seen = new Set()
        const cats = []
        for (const dot of this.pricingDots) {
          if (!seen.has(dot.label)) {
            seen.add(dot.label)
            cats.push({ label: dot.label, color: this.pricingVcColor(dot.label) })
          }
        }
        return cats
      },
    },
    methods: {
      pricingVcColor (label) {
        const colors = {
          'Ingredients & Intermediates': '#5CCC61',
          'Enabling Infrastructure': '#70C1FF',
          'End Products & Brands': '#FFC83D',
          'Processing & Technology': '#B5F167',
          'Market Access & Distribution': '#EF374E',
        }
        return colors[label] || '#9ca3af'
      },
      pricingTooltipStyle (dot) {
        const x = dot.x
        const y = 100 - dot.y
        const flipX = x > 70 ? `right: calc(${100 - x}% + 10px)` : `left: calc(${x}% + 10px)`
        const flipY = y > 70 ? `bottom: calc(${100 - y}% + 4px)` : `top: calc(${y}% + 4px)`
        return flipX + '; ' + flipY
      },
      onReportClick (reportName, path) {
        trackFoodleapInteraction('Foodleap Report Card', reportName)
        trackFoodleapCta('Intelligence', reportName, 'Reports')
        this.$router.push(path)
      },
      onPricingCta (tier, label, path) {
        trackFoodleapInteraction('Foodleap Pricing Tier', tier)
        trackFoodleapCta('Intelligence', label, tier)
        if (path) this.$router.push(path)
      },
    },
    mounted () {
      trackFoodleapPageView('/foodleap/intelligence', 'Foodleap - Intelligence')

      this._scrollTracker = createScrollDepthTracker('Foodleap Intelligence')
      this._scrollTracker.start()

      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible')
              this._observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.12 }
      )
      this.$el.querySelectorAll('.animate-on-scroll').forEach(el => {
        this._observer.observe(el)
      })

      // Track impressions for report cards and pricing tier cards
      this._impressionObserver = createImpressionObserver(0.4)
      this.$el.querySelectorAll('[data-content-name]').forEach(el => {
        this._impressionObserver.observe(el)
      })
    },
    beforeUnmount () {
      if (this._observer) this._observer.disconnect()
      if (this._impressionObserver) this._impressionObserver.disconnect()
      if (this._scrollTracker) this._scrollTracker.stop()
    },
  })
</script>

<style lang="scss">
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  // ── Keyframes ────────────────────────────────────────────────────────────────

  @keyframes fp-shimmer {
    0% {
      background-position: -200% center;
    }
    100% {
      background-position: 200% center;
    }
  }

  // ── Scroll-reveal ─────────────────────────────────────────────────────────────

  .foodleap-pricing .animate-on-scroll {
    opacity: 0;
    transform: translateY(28px);
    transition: opacity 0.65s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.65s cubic-bezier(0.22, 1, 0.36, 1);

    &.delay-1 {
      transition-delay: 0.12s;
    }

    &.delay-2 {
      transition-delay: 0.24s;
    }

    &.delay-3 {
      transition-delay: 0.36s;
    }

    &.is-visible {
      opacity: 1;
      transform: translateY(0);
    }
  }

  // ── Component styles ──────────────────────────────────────────────────────────

  .foodleap-pricing {
    font-family: 'Inter', sans-serif;

    // Foodleap brand colors
    $ps: #21244A; // primary-slate
    $pg: #5CCC61; // primary-green
    $blg: #D9E2DA; // brand-light-grey
    $svg: #B5F167; // secondary-vibrant-green
    $sy: #FFC83D; // secondary-yellow
    $sb: #70C1FF; // secondary-blue

    // ── Background utilities ──────────────────────────────────────────────────

    .bg-fp-background {
      background-color: #ffffff;
    }

    .bg-fp-primary {
      background-color: $ps;
    }

    .bg-fp-primary-container {
      background-color: $pg;
    }

    .bg-fp-primary-fixed {
      background-color: $blg;
    }

    .bg-fp-secondary-container {
      background-color: rgba(217, 226, 218, 0.4);
    }

    .bg-fp-secondary-container-30 {
      background-color: rgba(217, 226, 218, 0.2);
    }

    .bg-fp-surface-container-low {
      background-color: rgba(217, 226, 218, 0.2);
    }

    .bg-fp-surface-container-lowest {
      background-color: #ffffff;
    }

    .bg-fp-surface-container-high {
      background-color: rgba(217, 226, 218, 0.5);
    }

    .bg-fp-surface-container-highest {
      background-color: rgba(217, 226, 218, 0.3);
    }

    .bg-fp-surface-container {
      background-color: rgba(217, 226, 218, 0.3);
    }

    .bg-fp-deep-dark {
      background-color: $ps;
    }

    .bg-fp-primary-5 {
      background-color: rgba(33, 36, 74, 0.04);
    }

    .bg-fp-white-10 {
      background-color: rgba(255, 255, 255, 0.1);
    }

    // ── Text utilities ────────────────────────────────────────────────────────

    .text-fp-on-surface {
      color: $ps;
    }

    .text-fp-on-surface-variant {
      color: rgba(33, 36, 74, 0.6);
    }

    .text-fp-primary {
      color: $ps;
    }

    .text-fp-secondary {
      color: $pg;
    }

    .text-fp-tertiary {
      color: $sy;
    }

    .text-fp-secondary-fixed-dim {
      color: $svg;
    }

    .text-fp-on-secondary-fixed-variant {
      color: $ps;
    }

    .text-fp-white-90 {
      color: rgba(255, 255, 255, 0.9);
    }

    // ── Border utilities ──────────────────────────────────────────────────────

    .border-fp-primary {
      border-color: $ps;
    }

    .border-fp-outline {
      border-color: rgba(33, 36, 74, 0.2);
    }

    .border-fp-outline-variant-10 {
      border-color: rgba(217, 226, 218, 0.5);
    }

    .border-fp-white-5 {
      border-color: rgba(255, 255, 255, 0.05);
    }

    .border-fp-white-20 {
      border-color: rgba(255, 255, 255, 0.2);
    }

    // ── Report card color utilities ───────────────────────────────────────────

    .bg-fp-yellow-10 {
      background-color: rgba(255, 200, 61, 0.1);
    }

    .bg-fp-green-10 {
      background-color: rgba(92, 204, 97, 0.1);
    }

    .bg-fp-blue-10 {
      background-color: rgba(112, 193, 255, 0.1);
    }

    .text-fp-yellow {
      color: $sy;
    }

    .text-fp-blue {
      color: $sb;
    }

    .hover\:border-fp-yellow:hover {
      border-color: $sy;
    }

    .hover\:border-fp-green:hover {
      border-color: $pg;
    }

    .hover\:border-fp-blue:hover {
      border-color: $sb;
    }

    // ── Hover utilities ───────────────────────────────────────────────────────

    .hover\:bg-fp-surface-container-high:hover {
      background-color: rgba(217, 226, 218, 0.5);
    }

    .hover\:bg-fp-surface-container-low:hover {
      background-color: rgba(217, 226, 218, 0.2);
    }

    .hover\:text-fp-primary:hover {
      color: $ps;
    }

    // ── Gradient utilities ────────────────────────────────────────────────────

    .bg-gradient-primary {
      background-color: $ps;
    }

    .text-gradient-primary {
      background: linear-gradient(135deg, $ps 0%, darken($pg, 5%) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    // ── Special components ────────────────────────────────────────────────────

    .glass-panel {
      background: rgba(255, 255, 255, 0.85);
      backdrop-filter: blur(24px);
    }

    .shadow-ambient {
      box-shadow: 0 12px 40px rgba(33, 36, 74, 0.06);
    }

    .fp-img-overlay {
      background: linear-gradient(to top, rgba(255, 255, 255, 0.8), transparent);
    }

    // ── Shimmer CTA button ────────────────────────────────────────────────────

    .fp-btn-primary {
      position: relative;
      overflow: hidden;
      transition: transform 0.2s, box-shadow 0.2s;

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(105deg, transparent 40%, rgba(255, 255, 255, 0.12) 50%, transparent 60%);
        background-size: 200% 100%;
        opacity: 0;
        transition: opacity 0.2s;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 20px 40px rgba(33, 36, 74, 0.25);

        &::before {
          opacity: 1;
          animation: fp-shimmer 0.6s ease forwards;
        }
      }

      &:active {
        transform: translateY(0) scale(0.98);
      }
    }

    // ── Use-case icon hover ───────────────────────────────────────────────────

    .fp-use-case-icon-wrap {
      transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .fp-use-case-icon-wrap:hover {
      transform: scale(1.12) translateY(-4px);
    }

    // ── Hero section ──────────────────────────────────────────────────────────

    > section:first-of-type {
      background: radial-gradient(circle at top right, $blg 0%, #ffffff 60%) !important;
    }
  }
</style>
